/**
 * Candidate-surface dwell measurement — Phase 1 of the Team Vote sponsor
 * plan (docs/planning/team_vote_sponsor_surfaces.md in the sub-manager repo).
 *
 * Measures how long a visitor actually looks at the three candidate
 * surfaces before any sponsor treatment exists, so placement decisions are
 * made on evidence. One `cand_<surface>_<bucket>` pixel fires per surface
 * visit, on leave — to the same heartbeat endpoint and slug convention the
 * iOS app's candidate surfaces use, so the counts land in the existing
 * Pulse rollup with no new infrastructure.
 *
 * Rules this must never break:
 * - Candidate pings are a COUNTER, not inventory. They must never write to
 *   pulse_sponsor_exposures or wear a sponsor/viewability shape.
 * - No identifiers. The pixel carries an event slug and nothing else —
 *   same privacy contract as pulse.ts.
 * - Fail silent, defer to idle where possible, never contend with the page.
 * - Dwell counts VISIBLE time only. A tab left open in the background is
 *   not attention, and the whole point is an honest dwell distribution.
 */

const HEARTBEAT_PIXEL_URL =
  "https://etrspsphgbkapczqxboy.supabase.co/functions/v1/logo-transparent";

/** Route → candidate surface. Anything unlisted is not measured. */
export function surfaceForPath(pathname: string): string | null {
  if (pathname === "/teams") return "vote_teams";
  if (/^\/rsvp\/[^/]+$/.test(pathname)) return "vote_rsvp";
  if (/^\/game\/[^/]+$/.test(pathname)) return "vote_game_detail";
  return null;
}

/**
 * Dwell bucket labels, matching the Pulse rollup's allow-list exactly.
 * The three fine buckets exist because the RSVP kill criterion is
 * "sub-5-second answer-and-close" — the app's coarsest 0_30s bucket
 * cannot answer that question.
 */
export function bucketForSeconds(seconds: number): string {
  if (seconds < 5) return "0_5s";
  if (seconds < 15) return "5_15s";
  if (seconds < 30) return "15_30s";
  if (seconds < 120) return "30s_2m";
  if (seconds < 300) return "2_5m";
  if (seconds < 600) return "5_10m";
  if (seconds < 1800) return "10_30m";
  if (seconds < 3600) return "30_60m";
  return "60m_plus";
}

function firePixel(event: string) {
  try {
    const url = `${HEARTBEAT_PIXEL_URL}?e=${encodeURIComponent(event)}&t=${Date.now()}`;
    // fetch keepalive so the request survives navigation/pagehide; no-store
    // so a cached pixel response cannot swallow the server-side count.
    void fetch(url, { method: "GET", keepalive: true, cache: "no-store" }).catch(
      () => {
        /* never throw into the host page */
      },
    );
  } catch {
    /* noop */
  }
}

/**
 * Visibility-aware dwell tracker. Create one, `enter(pathname)` on every
 * route change, and it reports the previous surface when you leave it
 * (route change to a non-surface, another surface, or pagehide).
 *
 * Visits under one second are dropped: redirects and back-button flickers
 * are navigation noise, not a dwell observation — and 1–5s remains
 * observable as the 0_5s bounce bucket.
 */
export function createDwellTracker(send: (event: string) => void = firePixel) {
  let surface: string | null = null;
  let visibleMs = 0;
  let visibleSince: number | null = null;

  const now = () => Date.now();
  const pageVisible = () =>
    typeof document !== "undefined" && document.visibilityState === "visible";

  function settle() {
    if (visibleSince !== null) {
      visibleMs += now() - visibleSince;
      visibleSince = null;
    }
  }

  function flush() {
    settle();
    if (surface !== null && visibleMs >= 1000) {
      send(`cand_${surface}_${bucketForSeconds(visibleMs / 1000)}`);
    }
    surface = null;
    visibleMs = 0;
  }

  return {
    enter(pathname: string) {
      const next = surfaceForPath(pathname);
      if (next === surface) return;
      flush();
      surface = next;
      visibleSince = surface !== null && pageVisible() ? now() : null;
    },
    visibilityChanged() {
      if (surface === null) return;
      if (pageVisible()) {
        if (visibleSince === null) visibleSince = now();
      } else {
        settle();
      }
    },
    /** pagehide: the last chance to report the surface being left. */
    pageHide() {
      flush();
    },
  };
}
